// Constantes COMPARTIDAS (>1 consumidor). Las exclusivas de un componente viven
// colocadas en su carpeta.
//
// Provincias: UNA sola fuente canónica (`PROVINCES`). El código de UI y de datos
// debe derivar de aquí (slugs, búsquedas por slug, opciones de select) en lugar
// de mantener listas paralelas con la misma información.

import type { Province } from '@/app/lib/types'

/** Slug por defecto: «todas las localizaciones». */
export const ALL_LOCATIONS = 'todos'

/**
 * Mínimo de caracteres que debe tener un término de búsqueda escrito
 * libremente para que `/search` devuelva resultados (FR-002 —
 * specs/014-search-page). No aplica a una sugerencia elegida del
 * autocompletado (FR-003). Vive aquí (no en `lib/api/api.ts`) para que un
 * Client Component (`SearchInput`) pueda importarlo sin arrastrar el
 * api-client entero a su bundle (Principio IV) — `getSearch` la aplica como
 * regla real (research.md §2); `SearchInput` la reutiliza solo para no
 * navegar a una búsqueda que ya se sabe vacía.
 */
export const MIN_QUERY_LENGTH = 4

/** Cookie que persiste la localización activa (legible en server y client). */
export const LOCATION_COOKIE = 'location'

/**
 * Cookie de dominio compartido que escribe el sistema de identificación
 * central de El Corte Inglés al iniciar/cerrar sesión. Valor opaco; se
 * considera "sin sesión" si la cookie no existe o vale `'not_authenticated'`.
 * Ver `specs/007-header-login-session/research.md` (Decisión 2).
 */
export const SESSION_STATE_COOKIE = 'session_state'

/** Valor de `SESSION_STATE_COOKIE` cuando el usuario no tiene sesión iniciada. */
export const NOT_AUTHENTICATED = 'not_authenticated'

/**
 * Cookie de dominio compartido con el nombre del usuario con sesión
 * iniciada, legible en cliente (la escribe el sistema de identificación
 * central; la web anterior, `edo-entradas`, la lee igual para su «Hola,
 * nombre»). Codificada como formulario: los espacios llegan como `+`.
 */
export const USER_NAME_COOKIE = 'user_name'

/**
 * Id del elemento ancla de la banda de búsqueda de la home (`SearchBand`).
 * Contrato compartido: `Header` lo observa con `IntersectionObserver` para
 * decidir su estado visual (transparente/integrado); `SearchBand` lo expone.
 */
export const SEARCH_BAND_ANCHOR_ID = 'search-band-anchor'

// --- Listados virtualizados (PLP: agenda, lo-más-vendido, search, postsale,
// subcategoría) --- Configuración compartida del grid con scroll infinito
// (`InfiniteVirtualizedList` + `useResponsiveColumns`). Antes estaba duplicada en
// el `constants.ts` de cada página con valores idénticos.

/** `rootMargin` del sentinela de scroll infinito: dispara la siguiente página
 * cuando el final del listado se acerca a 400px del viewport. */
export const SCROLL_THRESHOLD = '400px'

/** Separación entre columnas del grid (token `--spacing-03`). */
export const COLUMN_GAP = 'var(--spacing-03)'

/** `COLUMN_GAP` en px: entra en el cálculo de ancho de columna de
 * `useResponsiveColumns`, no solo en CSS. */
export const COLUMN_GAP_PX = 12

/** Separación vertical entre filas del grid, en px (mismo valor que `COLUMN_GAP`
 * — `--spacing-03`): entra en el cálculo de posiciones de `VirtualizedList`. */
export const ROW_GAP_PX = 12

/** Nombre del query param de página (deep-link `?page=N` y reflejo del scroll
 * infinito). Compartido entre `page.tsx`, filtros y resultados de cada PLP. */
export const PAGE_PARAM = 'page'

/**
 * Fuente canónica de provincias de España.
 * `code` = INE (1–52, `'53'` = todas) · `slug` = valor de backend · `label` = UI.
 */
export const PROVINCES: readonly Province[] = [
  { code: '53', slug: 'todos', label: 'España (todas)' },
  { code: '1', slug: 'alava', label: 'Álava' },
  { code: '2', slug: 'albacete', label: 'Albacete' },
  { code: '3', slug: 'alicante', label: 'Alicante' },
  { code: '4', slug: 'almeria', label: 'Almería' },
  { code: '33', slug: 'asturias', label: 'Asturias' },
  { code: '5', slug: 'avila', label: 'Ávila' },
  { code: '6', slug: 'badajoz', label: 'Badajoz' },
  { code: '7', slug: 'baleares', label: 'Baleares' },
  { code: '8', slug: 'barcelona', label: 'Barcelona' },
  { code: '9', slug: 'burgos', label: 'Burgos' },
  { code: '10', slug: 'caceres', label: 'Cáceres' },
  { code: '11', slug: 'cadiz', label: 'Cádiz' },
  { code: '39', slug: 'cantabria', label: 'Cantabria' },
  { code: '12', slug: 'castellon', label: 'Castellón' },
  { code: '51', slug: 'ceuta', label: 'Ceuta' },
  { code: '13', slug: 'ciudad-real', label: 'Ciudad Real' },
  { code: '14', slug: 'cordoba', label: 'Córdoba' },
  { code: '15', slug: 'coruna', label: 'A Coruña' },
  { code: '16', slug: 'cuenca', label: 'Cuenca' },
  { code: '17', slug: 'girona', label: 'Girona' },
  { code: '18', slug: 'granada', label: 'Granada' },
  { code: '19', slug: 'guadalajara', label: 'Guadalajara' },
  { code: '20', slug: 'guipuzcoa', label: 'Guipúzcoa' },
  { code: '21', slug: 'huelva', label: 'Huelva' },
  { code: '22', slug: 'huesca', label: 'Huesca' },
  { code: '23', slug: 'jaen', label: 'Jaén' },
  { code: '24', slug: 'leon', label: 'León' },
  { code: '25', slug: 'lleida', label: 'Lleida' },
  { code: '26', slug: 'rioja', label: 'La Rioja' },
  { code: '27', slug: 'lugo', label: 'Lugo' },
  { code: '28', slug: 'madrid', label: 'Madrid' },
  { code: '29', slug: 'malaga', label: 'Málaga' },
  { code: '30', slug: 'murcia', label: 'Murcia' },
  { code: '31', slug: 'navarra', label: 'Navarra' },
  { code: '32', slug: 'ourense', label: 'Ourense' },
  { code: '34', slug: 'palencia', label: 'Palencia' },
  { code: '35', slug: 'palmas', label: 'Las Palmas' },
  { code: '36', slug: 'pontevedra', label: 'Pontevedra' },
  { code: '37', slug: 'salamanca', label: 'Salamanca' },
  { code: '38', slug: 'santa-cruz-de-tenerife', label: 'Santa Cruz de Tenerife' },
  { code: '40', slug: 'segovia', label: 'Segovia' },
  { code: '41', slug: 'sevilla', label: 'Sevilla' },
  { code: '42', slug: 'soria', label: 'Soria' },
  { code: '43', slug: 'tarragona', label: 'Tarragona' },
  { code: '44', slug: 'teruel', label: 'Teruel' },
  { code: '45', slug: 'toledo', label: 'Toledo' },
  { code: '46', slug: 'valencia', label: 'Valencia' },
  { code: '47', slug: 'valladolid', label: 'Valladolid' },
  { code: '48', slug: 'vizcaya', label: 'Vizcaya' },
  { code: '49', slug: 'zamora', label: 'Zamora' },
  { code: '50', slug: 'zaragoza', label: 'Zaragoza' },
  { code: '52', slug: 'melilla', label: 'Melilla' },
]

/** Allow-list de slugs válidos (validación de segmentos de URL). Derivada. */
export const PROVINCE_SLUGS: readonly string[] = PROVINCES.map((p) => p.slug)

/** Búsqueda O(1) de provincia por slug. Derivada. */
export const PROVINCE_BY_SLUG: ReadonlyMap<string, Province> = new Map(
  PROVINCES.map((p) => [p.slug, p]),
)

/** Devuelve la provincia por slug, o la opción «todas» si no existe. */
export function getProvince(slug: string): Province {
  return PROVINCE_BY_SLUG.get(slug) ?? PROVINCES[0]
}
